import React, { useEffect, useState, useMemo } from "react";
import { fetchGetProductosParaVenta } from "../services/productoService";
import { useUserContext } from "../hooks/UserContext";
import { ListaPrecios as ListaPreciosModel } from "../models/ProductoModel";
import { Box, CircularProgress, Typography, Alert, TextField, Button } from "@mui/material";
import DynamicGrid from "../components/DynamicTable";
import LocalSelector from "./components/LocalSelector";
import { useNavigate } from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";
import { useAppConfig } from "../hooks/AppConfigContext";
import { isAbortError } from "../services/fetchService";

export const listaPreciosGridColumnDefinitions = {
  'CODIGO': { Header: 'Código', Editable: false },
  'PRODUCTO': { Header: 'Producto', Editable: false },
  'CONTADO': { Header: 'Contado $', Editable: false, IsNumber: true, Currency: true },
  'MAYOR': { Header: 'Mayor $', Editable: false, IsNumber: true, Currency: true },
  'MENOR': { Header: 'Menor $', Editable: false, IsNumber: true, Currency: true },
  'EDITAR': { Header: 'Acciones', Editable: false },
};

const ListaPrecios = () => {
  const { getUser, isLoggedIn } = useUserContext();
  const { productoVentaHeaders } = useAppConfig();
  const navigate = useNavigate();
  const [productos, setProductos] = useState<ListaPreciosModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [idLocal, setIdLocal] = useState<string | undefined>(undefined);
  const [filterText, setFilterText] = useState<string>("");

  const handleLocalChange = (idLocal: string | undefined) => {
    setIdLocal(idLocal);
  };

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterText(event.target.value);
  };

  // Filter products by code or description.
  const filteredProductos = useMemo(() => {
    if (!filterText) return productos;
    const normalizedFilter = filterText.toLowerCase();
    return productos.filter((producto) =>
      producto.CODIGO.toLowerCase().includes(normalizedFilter) ||
      producto.PRODUCTO.toLowerCase().includes(normalizedFilter)
    );
  }, [productos, filterText]);

  useEffect(() => {
    if (!filterText.trim() || filteredProductos.length !== 1) return;

    const clearSearchTimer = window.setTimeout(() => {
      setFilterText("");
    }, 30000);

    return () => window.clearTimeout(clearSearchTimer);
  }, [filterText, filteredProductos.length]);

  // Check if user is authorized
  const user = getUser();
  const hasPermission = user?.permissions?.includes("lista_precios");
  const canUpdatePrices = user?.permissions?.includes("actualizar_precios");
  const listaPreciosHeaders = {
    ...productoVentaHeaders,
    EDITAR: { Header: "Acciones", Editable: false }
  };

  useEffect(() => {
    if (!isLoggedIn() || !hasPermission) {
      return;
    }

    if (!idLocal) {
      setProductos([]);
      return;
    }

    const currentUser = getUser();
    if (!currentUser) {
      setError("Usuario no autenticado");
      return;
    }

    const controller = new AbortController();
    setLoading(true);
    setError(null);
    fetchGetProductosParaVenta(idLocal, currentUser, controller.signal)
      .then((data: ListaPreciosModel[]) => {
        setProductos(data);
      })
      .catch((error) => {
        if (isAbortError(error)) return;
        console.error("Error fetching products:", error);
        if (error.status === 404) {
          setError("No se encontraron productos");
        } else {
          setError("Error al cargar la lista de productos");
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [getUser, hasPermission, idLocal, isLoggedIn]);

  // Show unauthorized message if user doesn't have permission
  if (!isLoggedIn()) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Por favor, inicie sesión para acceder a esta página.</Alert>
      </Box>
    );
  }

  if (!hasPermission) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">No tiene permisos para acceder a la lista de precios.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Lista de Precios
      </Typography>

      <Box sx={{ mb: 3 }}>
        <LocalSelector onLocalChange={handleLocalChange} />
      </Box>

      {productos.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <TextField
            label="Buscar por código o producto"
            variant="outlined"
            fullWidth
            value={filterText}
            onChange={handleFilterChange}
            placeholder="Ingrese el código o nombre del producto..."
            helperText="Puede buscar por una parte del código o del nombre. Si queda un solo producto, la búsqueda se limpia después de 30 segundos."
          />
        </Box>
      )}

      {loading ? (
        <Box sx={{ textAlign: "center", mt: 3 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : filteredProductos.length > 0 ? (
        <DynamicGrid
          data={filteredProductos}
          customHeaderMap={listaPreciosHeaders}
          pageSize={10}
          removeIfNotCustom={true}
          customRenderers={
            canUpdatePrices
              ? {
                  EDITAR: (row, rowIndex) => (
                    <Button
                      onClick={() => navigate(`/updateproducto/${row.IDPRODUCTO}`)}
                      variant="contained"
                      color="primary"
                      size="small"
                      startIcon={<EditIcon />}
                    >
                      Editar
                    </Button>
                  ),
                }
              : undefined
          }
        />
      ) : productos.length > 0 ? (
        <Typography variant="body1" color="textSecondary">
          No se encontraron productos que coincidan con "{filterText}"
        </Typography>
      ) : (
        <Typography variant="body1" color="textSecondary">
          Seleccione un local para ver la lista de productos
        </Typography>
      )}
    </Box>
  );
};

export default ListaPrecios;
